import { SelectionModel } from "@angular/cdk/collections";
import { AfterViewInit, Component, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatTable } from "@angular/material/table";
import { Tools } from "@comunicare/comunicare-helper";
import { BILLING_TYPE, DataType, IAggregatedChargeItemsInfo, IFilter, ISimplifiedChargeItems } from "@comunicare/comunicare-interfaces";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { PeriodicElement } from "src/app/careplan-editor/views/careplan-editor-page/components/linkable-items-list/linkable-items-list.component";
import { Item } from "src/app/components/item-selector/item-selector.component";
import { PdfTypeChoiceComponent, PDF_TYPE_CONTEXT } from "src/app/components/patient-QR/pdf-type-choice/pdf-type-choice.component";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { InvoiceNotesDialogComponent } from "../invoice-notes-dialog/invoice-notes-dialog.component";

@Component({
  selector: "app-invoice-aggregated-table",
  templateUrl: "./invoice-aggregated-table.component.html",
  styleUrls: ["./invoice-aggregated-table.component.scss", "../../../../../assets/comunicare-table.scss"],
})
export class InvoiceAggregatedTableComponent implements AfterViewInit {
  @Input() data: IAggregatedChargeItemsInfo[];

  @ViewChild(MatTable) table: MatTable<unknown>;
  @ViewChildren("masterCheckbox") masterCheckbox: QueryList<MatCheckbox>;

  @Output() send = new EventEmitter<string>();
  @Output() updateStatus = new EventEmitter<{ identifiers: string[]; newStatus: BILLING_TYPE }>();
  @Output() reload = new EventEmitter<void>();

  protected onDestroy$ = new Subject<void>();

  public actionOnMultiple: boolean;
  public selection = new SelectionModel<{
    start: string;
    end: string;
    identification: string;
    renew: boolean;
    adherence: number;
    code: string;
    amount: string;
    status: BILLING_TYPE;
    type: string;
  }>(true, []);
  public allSelected = false;

  public BILLING_TYPE: BILLING_TYPE[] = Object.keys(BILLING_TYPE).map((key) => key as BILLING_TYPE);

  public displayedColumns: string[] = ["lastname", "firstname", "ssin", "internalId", "pseudoname", "startDate", "endDate", "status"];
  public chargeItemDisplayedColumns = [
    "startDate",
    "endDate",
    "identification",
    "renew",
    "adherence",
    "code",
    "amount",
    "status",
    "type",
    "actions",
  ];
  public dataType = DataType;
  public expandedElement: PeriodicElement | null;
  private expandedIndex: number;

  billingStatusClasses: { [key in BILLING_TYPE]: string } = {
    [BILLING_TYPE.PLANNED]: "planned",
    [BILLING_TYPE.BILLABLE]: "billable",
    [BILLING_TYPE.ACCUMULATED]: "accumulated",
    [BILLING_TYPE.NOT_BILLABLE]: "not-billable",
    [BILLING_TYPE.BILLED]: "billed",
  };
  filters: IFilter[];

  constructor(public responsiveDialog: ResponsiveDialogService) {}

  ngAfterViewInit(): void {
    // Watch selection
    this.selection.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      if (Tools.isDefined(this.expandedIndex)) {
        const selectedCount = this.selection.selected.length;
        const totalCount = this.data[this.expandedIndex].chargeItems.length;
        this.allSelected = selectedCount === totalCount;
        this.masterCheckbox.toArray()[this.expandedIndex].checked = this.allSelected;
        this.masterCheckbox.toArray()[this.expandedIndex].indeterminate = selectedCount > 0 && !this.allSelected;
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public activateCheckBox(): void {
    this.actionOnMultiple = !this.actionOnMultiple;
    if (this.actionOnMultiple) {
      this.chargeItemDisplayedColumns.unshift("select");
    } else {
      this.resetSelection();
    }
  }

  public resetSelection(): void {
    this.selection.clear();
    this.actionOnMultiple = false;
    if (this.chargeItemDisplayedColumns[0] === "select") {
      this.chargeItemDisplayedColumns.shift();
    }
  }

  public masterToggle(): void {
    if (this.selection.hasValue()) {
      this.selection.clear();
    } else {
      this.selection.select(...this.data[this.expandedIndex].chargeItems);
    }
  }

  public setDisplayedColumns(availableColumnItems: Item[]): void {
    this.chargeItemDisplayedColumns = this.actionOnMultiple
      ? ["select", ...availableColumnItems.filter((item) => item.checked).map((item) => item.value)]
      : [...availableColumnItems.filter((item) => item.checked).map((item) => item.value)];
  }

  public download(chargeItemId: string): void {
    this.responsiveDialog.open(
      PdfTypeChoiceComponent,
      { data: { context: PDF_TYPE_CONTEXT.CHARGE_ITEM, chargeItemIds: [chargeItemId], isAggregatedView: true }, disableClose: true },
      {},
      {
        minWidth: "auto",
        width: "auto",
        maxWidth: "auto",

        minHeight: "auto",
        height: "auto",
        maxHeight: "auto",
      }
    );
  }

  public onSend(chargeItemId: string): void {
    this.send.emit(chargeItemId);
  }

  public onUpdateStatus(chargeItemId: string, newStatus: BILLING_TYPE): void {
    this.updateStatus.emit({ identifiers: [chargeItemId], newStatus });
  }

  /** Checks whether an element is expanded. */
  public isExpanded(element: PeriodicElement): boolean {
    return this.expandedElement === element;
  }

  /** Toggles the expanded state of an element. */
  public toggle(element: any): void {
    this.expandedElement = this.isExpanded(element) ? null : element;
    const newIndex = this.data.findIndex((d) => d.caremateIdentifier === element?.caremateIdentifier);
    this.expandedIndex = newIndex === this.expandedIndex ? undefined : newIndex;
    this.selection.clear();
    if (this.masterCheckbox?.toArray()?.length && Tools.isDefined(this.expandedIndex)) {
      this.masterCheckbox.toArray()[this.expandedIndex].checked = false;
      this.masterCheckbox.toArray()[this.expandedIndex].indeterminate = false;
    }
  }

  public showNotes(chargeItem: ISimplifiedChargeItems): void {
    const dialogRef = this.responsiveDialog.open(InvoiceNotesDialogComponent, {
      data: { chargeItem },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.reload.emit();
      this.expandedElement = undefined;
      this.expandedIndex = undefined;
    });
  }
}
